import { v4 as uuidv4 } from "uuid";

import { add, isAfter } from "@utils/date";
import { StorageKeys } from "@utils/enums";
import { attemptMethod } from "@utils/general";

export enum CallServiceKey {
    Cart = "CART",
    Wishlist = "WISHLIST",
    Customer = "CUSTOMER",
}

type ShouldCallSessionPayload = {
    shouldLoad: boolean;
    timestamp: number;
    uuid?: string;
};

type ShouldCallLocalPayload = {
    timestamp: number;
    uuid?: string;
};

export default class CallService {
    private static makeKey(key: CallServiceKey) {
        return StorageKeys.ShouldCall + key;
    }

    private static getSessionItem(generatedKey: string) {
        const sessionItem = sessionStorage.getItem(generatedKey);
        return attemptMethod<ShouldCallSessionPayload | undefined>(
            () => (sessionItem ? JSON.parse(sessionItem) : undefined),
            undefined,
        );
    }

    private static getLocalItem(generatedKey: string) {
        const localItem = localStorage.getItem(generatedKey);
        return attemptMethod<ShouldCallLocalPayload | undefined>(
            () => (localItem ? JSON.parse(localItem) : undefined),
            undefined,
        );
    }

    private static getUUID(generatedKey: string) {
        const sessionItem = this.getSessionItem(generatedKey);
        return sessionItem?.uuid || uuidv4();
    }

    private static setItem(key: CallServiceKey, shouldLoad: boolean) {
        const generatedKey = this.makeKey(key);
        const uuid = this.getUUID(generatedKey);
        const payload = JSON.stringify({
            shouldLoad,
            uuid,
            timestamp: new Date().getTime(),
        });

        sessionStorage.setItem(generatedKey, payload);
        localStorage.setItem(generatedKey, payload);
    }

    static enable(key: CallServiceKey) {
        this.setItem(key, true);
    }

    static disable(key: CallServiceKey) {
        this.setItem(key, false);
    }

    static shouldCall(key: CallServiceKey, retention: Duration): boolean {
        const generatedKey = this.makeKey(key);
        const sessionItem = this.getSessionItem(generatedKey);
        const localItem = this.getLocalItem(generatedKey);

        let result = true;

        if (sessionItem) {
            const hasTimedOut = isAfter(
                new Date(),
                add(new Date(sessionItem.timestamp), retention),
            );

            result = !hasTimedOut && sessionItem.shouldLoad;
        }

        if (
            sessionItem &&
            !result &&
            localItem?.uuid &&
            localItem?.uuid !== sessionItem?.uuid
        ) {
            const hasTimeout = isAfter(
                new Date(localItem.timestamp),
                new Date(sessionItem.timestamp),
            );

            if (hasTimeout) {
                result = true;
            }
        }

        return result;
    }
}
